import React, { useState, useEffect, useMemo } from 'react';
import { suppliersApi } from '../lib/api';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { Plus, Truck, Search, Edit, Trash2, Building2, ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';
import { getApiErrorMessage } from '../lib/errorUtils';

const emptyForm = {
  name: '', code: '', contact_name: '', email: '', phone: '', mobile: '',
  address: '', postal_code: '', city: '', country: 'France', siret: '', notes: ''
};

const SuppliersPage = () => {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');

  useEffect(() => { fetchSuppliers(); }, []);

  const fetchSuppliers = async () => {
    try {
      setLoading(true);
      const res = await suppliersApi.getAll(search ? { search } : {});
      setSuppliers(res.data);
    } catch { toast.error('Erreur chargement fournisseurs'); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    const t = setTimeout(() => fetchSuppliers(), 300);
    return () => clearTimeout(t);
  }, [search]);

  const sortedSuppliers = useMemo(() => {
    return [...suppliers].sort((a, b) => {
      const valA = (a[sortBy] || '').toString().toLowerCase();
      const valB = (b[sortBy] || '').toString().toLowerCase();
      const cmp = valA.localeCompare(valB, 'fr');
      return sortOrder === 'asc' ? cmp : -cmp;
    });
  }, [suppliers, sortBy, sortOrder]);

  const toggleSort = (field) => {
    if (sortBy === field) setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    else { setSortBy(field); setSortOrder('asc'); }
  };

  const SortIcon = ({ field }) => {
    if (sortBy !== field) return <ArrowUpDown className="w-3 h-3 ml-1 opacity-30" />;
    return sortOrder === 'asc' ? <ArrowUp className="w-3 h-3 ml-1 text-amber-400" /> : <ArrowDown className="w-3 h-3 ml-1 text-amber-400" />;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editMode) {
        await suppliersApi.update(editId, form);
        toast.success('Fournisseur mis à jour');
      } else {
        await suppliersApi.create(form);
        toast.success('Fournisseur créé');
      }
      setShowDialog(false);
      setForm({ ...emptyForm });
      setEditMode(false);
      fetchSuppliers();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Erreur'));
    }
  };

  const handleEdit = (s) => {
    setForm({ name: s.name||'', code: s.code||'', contact_name: s.contact_name||'',
      email: s.email||'', phone: s.phone||'', mobile: s.mobile||'',
      address: s.address||'', postal_code: s.postal_code||'', city: s.city||'',
      country: s.country||'France', siret: s.siret||'', notes: s.notes||'' });
    setEditId(s.id); setEditMode(true); setShowDialog(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce fournisseur ?')) return;
    try { await suppliersApi.delete(id); toast.success('Fournisseur supprimé'); fetchSuppliers(); }
    catch (error) { toast.error(getApiErrorMessage(error, 'Erreur')); }
  };

  return (
    <div className="space-y-6" data-testid="suppliers-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading text-white">Fournisseurs</h1>
          <p className="text-zinc-400 text-sm">{suppliers.length} fournisseur(s)</p>
        </div>
        <Button onClick={() => { setForm({...emptyForm}); setEditMode(false); setShowDialog(true); }}
          className="bg-amber-500 hover:bg-amber-400 text-black" data-testid="create-supplier-button">
          <Plus className="w-4 h-4 mr-2" /> Nouveau fournisseur
        </Button>
      </div>

      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <div className="relative flex-1 w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <Input placeholder="Rechercher..." value={search} onChange={(e) => setSearch(e.target.value)}
                className="pl-10 bg-zinc-950 border-zinc-800 text-white" data-testid="search-suppliers" />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-zinc-500 font-semibold uppercase tracking-wider">Tri :</span>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-[130px] h-8 bg-zinc-950 border-zinc-800 text-white text-xs" data-testid="sort-suppliers">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  <SelectItem value="name" className="text-white">Nom</SelectItem>
                  <SelectItem value="code" className="text-white">Code</SelectItem>
                  <SelectItem value="city" className="text-white">Ville</SelectItem>
                  <SelectItem value="contact_name" className="text-white">Contact</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" className="h-8 border-zinc-700 text-zinc-400 hover:text-white px-2"
                onClick={() => setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')} data-testid="sort-suppliers-order">
                {sortOrder === 'asc' ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />}
                <span className="ml-1 text-xs">{sortOrder === 'asc' ? 'A→Z' : 'Z→A'}</span>
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <ScrollArea className="h-[calc(100vh-280px)]">
            <Table>
              <TableHeader>
                <TableRow className="border-zinc-800">
                  <TableHead className="text-zinc-400 cursor-pointer select-none hover:text-white" onClick={() => toggleSort('code')}>
                    <span className="flex items-center">Code<SortIcon field="code" /></span>
                  </TableHead>
                  <TableHead className="text-zinc-400 cursor-pointer select-none hover:text-white" onClick={() => toggleSort('name')}>
                    <span className="flex items-center">Nom<SortIcon field="name" /></span>
                  </TableHead>
                  <TableHead className="text-zinc-400 cursor-pointer select-none hover:text-white" onClick={() => toggleSort('contact_name')}>
                    <span className="flex items-center">Contact<SortIcon field="contact_name" /></span>
                  </TableHead>
                  <TableHead className="text-zinc-400 cursor-pointer select-none hover:text-white" onClick={() => toggleSort('city')}>
                    <span className="flex items-center">Ville<SortIcon field="city" /></span>
                  </TableHead>
                  <TableHead className="text-zinc-400">Téléphone</TableHead>
                  <TableHead className="text-zinc-400 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={6} className="text-center py-8">
                    <div className="w-6 h-6 border-2 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto" />
                  </TableCell></TableRow>
                ) : sortedSuppliers.length === 0 ? (
                  <TableRow><TableCell colSpan={6} className="text-center py-8 text-zinc-500">
                    <Truck className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>Aucun fournisseur</p>
                  </TableCell></TableRow>
                ) : sortedSuppliers.map(s => (
                  <TableRow key={s.id} className="border-zinc-800 hover:bg-zinc-900/50" data-testid={`supplier-row-${s.id}`}>
                    <TableCell className="font-mono text-cyan-400 text-xs">{s.code || '-'}</TableCell>
                    <TableCell className="text-white font-medium">{s.name}</TableCell>
                    <TableCell className="text-zinc-300 text-sm">{s.contact_name || '-'}</TableCell>
                    <TableCell className="text-zinc-400 text-sm">{s.city ? `${s.postal_code||''} ${s.city}` : '-'}</TableCell>
                    <TableCell className="text-zinc-400 text-sm">{s.phone || s.mobile || '-'}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button size="sm" variant="ghost" className="text-amber-400 hover:bg-amber-500/10 h-8 w-8 p-0"
                          onClick={() => handleEdit(s)}><Edit className="w-4 h-4" /></Button>
                        <Button size="sm" variant="ghost" className="text-red-400 hover:bg-red-500/10 h-8 w-8 p-0"
                          onClick={() => handleDelete(s.id)}><Trash2 className="w-4 h-4" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ScrollArea>
        </CardContent>
      </Card>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-white font-heading flex items-center gap-2">
              <Building2 className="w-5 h-5 text-amber-400" />
              {editMode ? 'Modifier le fournisseur' : 'Nouveau fournisseur'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-zinc-300">Nom / Raison sociale *</Label>
                <Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})}
                  className="bg-zinc-950 border-zinc-800 text-white" required data-testid="supplier-name-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">Code</Label>
                <Input value={form.code} onChange={(e) => setForm({...form, code: e.target.value})}
                  className="bg-zinc-950 border-zinc-800 text-white font-mono" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-zinc-300">Contact</Label>
                <Input value={form.contact_name} onChange={(e) => setForm({...form, contact_name: e.target.value})}
                  className="bg-zinc-950 border-zinc-800 text-white" />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">Email</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})}
                  className="bg-zinc-950 border-zinc-800 text-white" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-zinc-300">Téléphone</Label>
                <Input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})}
                  className="bg-zinc-950 border-zinc-800 text-white" />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">Mobile</Label>
                <Input value={form.mobile} onChange={(e) => setForm({...form, mobile: e.target.value})}
                  className="bg-zinc-950 border-zinc-800 text-white" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300">Adresse</Label>
              <Input value={form.address} onChange={(e) => setForm({...form, address: e.target.value})}
                className="bg-zinc-950 border-zinc-800 text-white" />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="text-zinc-300">Code postal</Label>
                <Input value={form.postal_code} onChange={(e) => setForm({...form, postal_code: e.target.value})}
                  className="bg-zinc-950 border-zinc-800 text-white" />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">Ville</Label>
                <Input value={form.city} onChange={(e) => setForm({...form, city: e.target.value})}
                  className="bg-zinc-950 border-zinc-800 text-white" />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">Pays</Label>
                <Input value={form.country} onChange={(e) => setForm({...form, country: e.target.value})}
                  className="bg-zinc-950 border-zinc-800 text-white" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-zinc-300">SIRET</Label>
                <Input value={form.siret} onChange={(e) => setForm({...form, siret: e.target.value})}
                  className="bg-zinc-950 border-zinc-800 text-white font-mono" />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">Notes</Label>
                <Input value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})}
                  className="bg-zinc-950 border-zinc-800 text-white" />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-4 border-t border-zinc-800">
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)} className="border-zinc-700">Annuler</Button>
              <Button type="submit" className="bg-amber-500 hover:bg-amber-400 text-black" data-testid="save-supplier-button">
                {editMode ? 'Enregistrer' : 'Créer'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SuppliersPage;
